import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Divider, Form, Modal, Spin } from "antd";
import CustomButton from "components/Common/buttons/CustomButton";
import { makeStyles } from "@material-ui/core/styles";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import { defaultNgiErrorMethod, successMessage } from "components/Common/responses/message";
import { createBundleAssignment, updateBundleAssignment } from "utils/api";
import { customDateFormatWithoutTime } from "components/Common/formatter";
import moment from "moment/moment";
import { USER_ROLES, USER_TENANT_NAME } from "utils/constants";
import NGISelect from "components/Common/inputs/NGISelect";
import { Option } from "antd/es/mentions";
import NGIDatePicker from "components/Common/inputs/NGIDatePicker";
import {
  ASSIGNMENTS_TYPES,
  BUNDLE_ASSIGNMENT_ACTIONS,
  REGISTRATION_CATEGORY_LIST,
} from "pages/price-management/Bundles/bundleDetails/constants";
import NGICheckbox from "components/Common/inputs/NGICheckbox";
import classnames from "classnames";
import { uniq } from "lodash";
import { useSelector } from "react-redux";
import { DictionariesSelector } from "store/dictionaries/selectors";
const useStyles = makeStyles({
  modal: {
    "& .ant-modal-content .ant-modal-body": {
      padding: "24px 50px !important",
    },
  },
  titleWithDescription: {
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    fontWeight: 600,

    borderBottom: "1px solid #0000000D",
    paddingBottom: 20,

    "&>span": {
      fontSize: 13,
      fontWeight: 300,
    },
  },
  container: {
    borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
    borderLeft: "none",
    borderRight: "none",
    marginBottom: 30,
    paddingBottom: 30,
    padding: "0 50px",
  },
  tabs: {
    "&.ant-tabs > .ant-tabs-nav, .ant-tabs > div > .ant-tabs-nav": {
      padding: "0px!important",
      margin: 0,
    },
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
  },
  stepsContainer: {
    padding: "20px 50px",
    backgroundColor: "#0000000D",
    border: "1px solid #0000000D",
  },
  withMargin: {
    marginRight: 10,
  },
  typeTitle: {
    textTransform: "capitalize",
  },

  formContainer: {
    display: "flex",
    flexDirection: "column",
  },
  formItem: {
    display: "flex",
    flex: "1!important",
    margin: "0px!important",

    "& .ant-row": {
      width: "100%",
    },
  },
  formRow: {
    display: "flex",
    padding: "10px 0",
    gap: 15,
  },
  formRowColumn: {
    flexDirection: "column",
  },
  fieldsContainer: {},
  rowTitle: {
    flex: 2,
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
  },
  subTitle: {
    fontSize: 11,
    fontWeight: 300,
    color: "#000000BF",
  },
  liteSelect: {
    "&.ant-select:not(.ant-select-customize-input) .ant-select-selector": {
      border: "2px solid #0000001A",
      borderRadius: 5,
    },
  },
  liteInput: {
    border: "2px solid #0000001A",
    borderRadius: 5,
  },
  liteDate: {
    border: "2px solid #0000001A",
    borderRadius: 5,
  },
  currency: {
    backgroundColor: "#0000000D",
    borderRadius: 2,
    fontSize: 9,
    color: "#666666",
    padding: "2px 8px",
  },
  optionValue: {
    "&>div": {
      color: "#8C8C8C",
      fontWeight: 300,
      fontSize: 9,
    },
  },
  selectedValue: {
    "& .ant-select-selection-item": {
      height: 30,
    },
  },
  disabledField: {
    flex: 1,
  },
  calculatedInput: {
    backgroundColor: "#0085FF0F!important",

    "&> input": {
      backgroundColor: "transparent!important",
    },
  },
  basePriceContainer: {
    display: "flex",
    justifyContent: "space-between",
    backgroundColor: "#0000000A",
    padding: 15,
    fontWeight: 300,
    color: "#000000BF",
    marginBottom: 10,
  },
  amountContainer: {
    display: "flex",
    alignItems: "center",
    color: "#000000BF",
    fontWeight: 700,

    "&>span:first-child": {
      marginRight: 10,
    },
  },
  textEnd: {
    textAlign: "end",
  },
});

const NewBundleAssignmentModal = ({ bundleId, data, onClose, customerGroupList, update }) => {
  const classes = useStyles();
  const [form] = Form.useForm();
  const [isLoading, setLoader] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [type, setType] = useState(null);

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        ...data,
        activationDate: moment(data.activationDate),
        deactivationDate: data.deactivationDate ? moment(data.deactivationDate) : null,
      });
      setType(data.type);
    }
  }, []);

  const {
    list: { depot },
  } = useSelector(DictionariesSelector);

  const openConfirmationModal = useCallback(e => {
    e?.preventDefault();
    setIsConfirmationOpen(true);
  }, []);
  const closeConfirmationModal = useCallback(() => setIsConfirmationOpen(false), []);

  const onSuccessClose = useCallback(() => {
    update();
    onClose();
  }, []);

  const onCreate = useCallback(
    values => {
      const { type, depots, customerGroup, registrationCategory } = values;
      setLoader(true);
      const request = !!data ? updateBundleAssignment : createBundleAssignment;
      const preparedData = !!data
        ? {
            action: BUNDLE_ASSIGNMENT_ACTIONS.EDIT,
            deactivationDate: customDateFormatWithoutTime(values?.deactivationDate),
          }
        : {
            bundleId,
            type,
            isPrimary: !!values.isPrimary,
            activationDate: customDateFormatWithoutTime(values.activationDate),
            ...(!!values?.registrationCategory && {
              registrationCategory,
            }),
            ...(!!values?.customerGroup && {
              customerGroup,
            }),
            ...(!!values?.depots && { depots: depots === "ALL DEPOTS" ? depotList : depots }),

            ...(!!values?.deactivationDate && {
              deactivationDate: customDateFormatWithoutTime(values?.deactivationDate),
            }),
          };

      request(preparedData, data?.id)
        .then(() => {
          setTimeout(() => {
            setLoader(false);
            successMessage(`Bundle Assignment was ${!!data ? "updated" : "created"} successfully`);
            onClose();
            update();
          }, 500);
        })
        .catch(error => {
          setLoader(false);
          defaultNgiErrorMethod(error);
        });
    },
    [form, bundleId, data]
  );

  const onTypeChange = useCallback(
    value => {
      setType(value);
      form.setFieldsValue({
        depots: [],
        customerGroup: null,
      });
    },
    [form]
  );

  const onDepotChange = useCallback(
    value => {
      if (value[value.length - 1] === "ALL DEPOTS" && value.length >= 1) {
        form.setFieldValue("depots", "ALL DEPOTS");
        return;
      }
      form.setFieldValue(
        "depots",
        value.filter(item => item !== "ALL DEPOTS")
      );
    },
    [form]
  );

  const depotList = useMemo(() => depot[USER_TENANT_NAME()] && uniq(depot[USER_TENANT_NAME()]), [depot]);

  return (
    <Modal
      title={
        <div className={classes.titleWithDescription}>
          {data ? "Edit" : "New"} Bundle Assignments
          <span>Please fill all mandatory fields</span>
        </div>
      }
      open
      onCancel={openConfirmationModal}
      closable={false}
      className={classes.modal}
      footer={[]}
    >
      <Spin spinning={isLoading}>
        <Form form={form} onFinish={onCreate} name="newBundle" layout="inline" className={classes.formContainer}>
          <div className={classes.fieldsContainer}>
            <div className={classes.formRow}>
              <div className={classes.rowTitle}>
                <div className={classes.title}>Target Type*</div>
                <div className={classes.subTitle}>Select between consumption or time based bundles</div>
              </div>
              <Form.Item
                className={classes.formItem}
                name="type"
                rules={[{ required: true, message: "Please enter value" }]}
              >
                <NGISelect
                  disabled={!!data}
                  onChange={onTypeChange}
                  showSearch
                  className={classes.liteSelect}
                  placeholder="Select Type"
                >
                  {ASSIGNMENTS_TYPES.map(item => (
                    <Option key={`${item}-option`} value={item}>
                      {item}
                    </Option>
                  ))}
                </NGISelect>
              </Form.Item>
            </div>
            {type && (
              <>
                <Divider />

                {type === "Depot" && (
                  <div className={classnames(classes.formRow, classes.formRowColumn)}>
                    <div className={classes.rowTitle}>
                      <div className={classes.title}>Select Depot*</div>
                      <div className={classes.subTitle}>Select depots from the list</div>
                    </div>
                    <Form.Item
                      className={classes.formItem}
                      name="depots"
                      rules={[{ required: true, message: "Please select value" }]}
                    >
                      <NGISelect
                        disabled={!!data}
                        mode="multiple"
                        className={classes.liteSelect}
                        showSearch
                        placeholder="Select depot"
                        allowClear
                        onChange={onDepotChange}
                      >
                        <Option value={"ALL DEPOTS"}>ALL DEPOTS</Option>
                        {depotList?.sort()?.map(item => (
                          <Option key={item} value={item}>
                            {item}
                          </Option>
                        ))}
                      </NGISelect>
                    </Form.Item>
                  </div>
                )}

                {type === "CustomerGroup" && (
                  <div className={classes.formRow}>
                    <div className={classes.rowTitle}>
                      <div className={classes.title}>Customer Group*</div>
                      <div className={classes.subTitle}>Select customer group from the list</div>
                    </div>
                    <Form.Item
                      className={classes.formItem}
                      name="customerGroup"
                      rules={[{ required: true, message: "Please select value" }]}
                    >
                      <NGISelect disabled={!!data} className={classes.liteSelect} placeholder="Select value">
                        {customerGroupList.map(item => (
                          <Option key={item.id} value={item.id}>
                            {item.name}
                          </Option>
                        ))}
                      </NGISelect>
                    </Form.Item>
                  </div>
                )}

                {["Depot", "Default"].includes(type) && (
                  <div className={classes.formRow}>
                    <div className={classes.rowTitle}>
                      <div className={classes.title}>Customer Category*</div>
                      <div className={classes.subTitle}>Select between commercial or residential</div>
                    </div>
                    <Form.Item
                      className={classes.formItem}
                      name="registrationCategory"
                      rules={[{ required: true, message: "Please select value" }]}
                    >
                      <NGISelect disabled={!!data} showSearch className={classes.liteSelect} placeholder="Select value">
                        {REGISTRATION_CATEGORY_LIST.map(item => (
                          <Option key={item} value={item}>
                            {item}
                          </Option>
                        ))}
                      </NGISelect>
                    </Form.Item>
                  </div>
                )}

                <div className={classes.formRow}>
                  <div className={classes.rowTitle}>
                    <div className={classes.title}>Bundle Assign Start Date*</div>
                    <div className={classes.subTitle}>
                      Set the first day this bundle will be available for activation
                    </div>
                  </div>
                  <Form.Item
                    className={classes.formItem}
                    name="activationDate"
                    rules={[{ required: true, message: "Please select date" }]}
                  >
                    <NGIDatePicker
                      disabled={!!data}
                      onChange={() => {
                        form.setFieldValue("deactivationDate", null);
                      }}
                      disableDates={current => moment() >= current}
                      className={classes.liteInput}
                      placeholder="Bundle Start Date"
                    />
                  </Form.Item>
                </div>
                <div className={classes.formRow}>
                  <div className={classes.rowTitle}>
                    <div className={classes.title}>Bundle Assign End Date (Optional)</div>
                    <div className={classes.subTitle}>
                      Set the last day this bundle will be available for activation
                    </div>
                  </div>
                  <Form.Item className={classes.formItem} name="deactivationDate">
                    <NGIDatePicker
                      allowClear
                      disableDates={current =>
                        form.getFieldValue("activationDate")?.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }) >
                        current
                      }
                      className={classes.liteInput}
                      placeholder="Bundle End Date"
                    />
                  </Form.Item>
                </div>

                <div className={classes.formRow}>
                  <div className={classes.rowTitle}>
                    <div className={classes.title}>Assign as Primary</div>
                    <div className={classes.subTitle}>
                      Assign this bundle as default. Will overwrite previous default bundles
                    </div>
                  </div>
                  <Form.Item
                    valuePropName="checked"
                    className={classnames(classes.formItem, classes.textEnd)}
                    name="isPrimary"
                  >
                    <NGICheckbox disabled={!!data} />
                  </Form.Item>
                </div>
              </>
            )}
          </div>

          <Divider />

          <div className={classes.actions}>
            <CustomButton onClick={openConfirmationModal} type="primary" size="small" color="outlined" text="Cancel" />

            <CustomButton
              roles={[USER_ROLES.IT_OPERATIONS, USER_ROLES.IT_OPERATIONS_LEADERSHIP]}
              size="small"
              text="ASSIGN BUNDLE"
              type="primary"
            />
          </div>
        </Form>
      </Spin>

      {isConfirmationOpen && (
        <ConfirmationModal
          description="The Bundle Assignments creation is in progress. Are you sure you want to discard the changes?"
          onCancel={closeConfirmationModal}
          onConfirm={onSuccessClose}
        />
      )}
    </Modal>
  );
};

export default NewBundleAssignmentModal;
